<template>
  <div class="result-list-container nav-content-inset">
    <ImageBar title="测试历史" height="80px"/>
    <div class="result-content" v-loading="loading">
      <div class="toolbar">
        <span class="toolbar-title">历史记录</span>
        <div class="toolbar-actions">
          <el-button @click="fetchResults">刷新</el-button>
          <el-button
            type="danger"
            :disabled="selectedRows.length === 0"
            @click="batchDelete"
          >
            删除选中 ({{ selectedRows.length }})
          </el-button>
        </div>
      </div>

      <div v-if="results.length > 0" class="result-cards result-cards--mobile">
        <div
          v-for="row in results"
          :key="row.id"
          class="result-card"
          @click="viewDetail(row)"
        >
          <div class="result-card__head">
            <span class="result-card__name">{{ row.examName }}</span>
            <el-button type="primary" size="small" link @click.stop="viewDetail(row)">
              查看详情
            </el-button>
          </div>
          <div class="result-card__meta">
            <span>用时 {{ formatDuration(row.totalTime) }}</span>
            <span>{{ formatDate(row.createdAt) }}</span>
          </div>
          <div class="result-card__actions" @click.stop>
            <el-checkbox
              :model-value="isRowSelected(row)"
              @change="(v) => toggleRowSelect(row, v)"
            >
              选中
            </el-checkbox>
          </div>
        </div>
      </div>

      <div class="result-table-wrap result-table-wrap--desktop">
        <el-table
          ref="tableRef"
          :data="results"
          style="width: 100%"
          @selection-change="handleSelectionChange"
        >
          <el-table-column type="selection" width="48" />
          <el-table-column prop="examName" label="测评名称" min-width="140" />
          <el-table-column prop="totalTime" label="用时" width="100">
            <template #default="scope">
              {{ formatDuration(scope.row.totalTime) }}
            </template>
          </el-table-column>
          <el-table-column prop="createdAt" label="完成时间" min-width="160">
            <template #default="scope">
              {{ formatDate(scope.row.createdAt) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="100" fixed="right">
            <template #default="scope">
              <el-button type="primary" size="small" link @click="viewDetail(scope.row)">
                查看详情
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <el-empty v-if="!loading && results.length === 0" description="暂无测试记录" />
    </div>
  </div>
</template>

<script>
import { goToLogin } from '@/utils/loginFlow'
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import { ElMessage, ElMessageBox } from 'element-plus'
import ImageBar from './components/ImageBar.vue'
import { formatDuration } from '@/utils/formatDuration'
import { isExamRouteKeyNumeric } from '@/utils/examPricing'

export default {
  name: 'TestResultListView',
  components: {
    ImageBar
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const loading = ref(false)
    const results = ref([])
    const selectedRows = ref([])
    const tableRef = ref(null)

    const resolvedExamId = ref(0)

    const resolveRouteExamId = async () => {
      const key = String(route.params.id || '').trim()
      if (!key) return 0
      if (isExamRouteKeyNumeric(key)) {
        return Number(key)
      }
      const token = localStorage.getItem('jwt_token')
      const seg = encodeURIComponent(key)
      const url = token ? `/api/v1/exam/${seg}` : `/api/v1/exam/guest/${seg}`
      const headers = { 'Content-Type': 'application/json' }
      if (token) headers.Authorization = `Bearer ${token}`
      const response = await axios.get(url, { headers })
      if (response.data.code === 200 && response.data.data?.id) {
        return Number(response.data.data.id)
      }
      return 0
    }

    const checkLogin = () => {
      if (!localStorage.getItem('jwt_token')) {
        goToLogin(router)
        return false
      }
      return true
    }

    const authHeaders = () => ({
      Authorization: `Bearer ${localStorage.getItem('jwt_token')}`,
      'Content-Type': 'application/json'
    })

    const fetchResults = async () => {
      const eid = resolvedExamId.value
      if (!eid) {
        ElMessage.error('无效的测评ID')
        return
      }
      try {
        loading.value = true
        const response = await axios.post('/api/v1/result/list', {
          examId: eid
        }, { headers: authHeaders() })

        if (response.data.code === 200) {
          results.value = response.data.data?.results || []
          selectedRows.value = []
        } else if (response.data.code === 401) {
          ElMessage.error('请先登录')
          goToLogin(router)
        } else {
          ElMessage.error(response.data.message || '获取测试历史失败')
        }
      } catch (error) {
        console.error('Error fetching results:', error)
        ElMessage.error('获取测试历史失败')
      } finally {
        loading.value = false
      }
    }

    const formatDate = (timestamp) => {
      if (!timestamp) return ''
      const date = new Date(timestamp * 1000)
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const viewDetail = (result) => {
      router.push(`/exam/result/${result.id}`)
    }

    const handleSelectionChange = (rows) => {
      selectedRows.value = rows
    }

    const isRowSelected = (row) =>
      selectedRows.value.some((r) => r.id === row.id)

    const toggleRowSelect = (row, checked) => {
      if (checked) {
        if (!isRowSelected(row)) {
          selectedRows.value = [...selectedRows.value, row]
        }
      } else {
        selectedRows.value = selectedRows.value.filter((r) => r.id !== row.id)
      }
    }

    const batchDelete = async () => {
      if (selectedRows.value.length === 0) return
      try {
        await ElMessageBox.confirm(
          `确定要删除选中的 ${selectedRows.value.length} 条测试记录吗？此操作不可恢复。`,
          '确认删除',
          { type: 'warning' }
        )
        const resultIds = selectedRows.value.map((row) => row.id)
        const response = await axios.post('/api/v1/result/delete/batch', {
          examId: resolvedExamId.value,
          resultIds
        }, { headers: authHeaders() })

        if (response.data.code === 200) {
          ElMessage.success(response.data.message || '删除成功')
          await fetchResults()
        } else {
          ElMessage.error(response.data.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error deleting results:', error)
          ElMessage.error('删除失败')
        }
      }
    }

    onMounted(async () => {
      if (!checkLogin()) return
      resolvedExamId.value = await resolveRouteExamId()
      if (!resolvedExamId.value) {
        ElMessage.error('测评不存在或简称无效')
        return
      }
      fetchResults()
    })

    return {
      loading,
      results,
      selectedRows,
      tableRef,
      formatDate,
      formatDuration,
      viewDetail,
      handleSelectionChange,
      isRowSelected,
      toggleRowSelect,
      fetchResults,
      batchDelete
    }
  }
}
</script>

<style scoped>
.result-list-container {
  max-width: 1800px;
  margin: 0 auto;
  padding: 20px;
  box-sizing: border-box;
}

.result-content {
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
}

.toolbar-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.toolbar-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.result-cards--mobile {
  display: none;
}

.result-table-wrap--desktop {
  width: 100%;
}

.result-card {
  background: #fff;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.result-card__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
}

.result-card__name {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  line-height: 1.4;
  flex: 1;
  min-width: 0;
  word-break: break-word;
}

.result-card__meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: #909399;
  margin-bottom: 10px;
}

.result-card__actions {
  padding-top: 8px;
  border-top: 1px solid var(--el-border-color-extra-light);
}

@media (max-width: 767px) {
  .result-list-container {
    padding: 0;
    max-width: 100%;
  }

  .result-content {
    padding: 12px;
    border-radius: 0;
    box-shadow: none;
  }

  .toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .toolbar-actions {
    width: 100%;
  }

  .toolbar-actions .el-button {
    flex: 1;
    margin: 0;
  }

  .result-cards--mobile {
    display: block;
  }

  .result-table-wrap--desktop {
    display: none;
  }
}
</style>
